import { useEffect, useRef, useState } from 'react';
import { useToast } from '../hooks/useToast';
import { useAfterWrite } from '../hooks/useAfterWrite';
import { useBusy, useBusyKeys } from '../hooks/useBusy';
import { useLoad } from '../hooks/useLoad';
import { useConfirm } from '../hooks/useConfirm';
import { Link, useNavigate } from 'react-router-dom';
import { useUrlTab } from '../hooks/useUrlParam';
import { useAuthStore } from '../store/auth';
import { useI18n } from '../i18n';
import { useLabels } from '../i18n/labels';
import Avatar from '../components/shared/Avatar';
import RoleBadge from '../components/shared/RoleBadge';
import StatusBadge from '../components/shared/StatusBadge';
import TabBar from '../components/shared/TabBar';
import ResolveActions from '../components/shared/ResolveActions';
import api, { uploadAvatar, deleteAvatar, getMe, getMyAthlete, updateMyAthlete, getMyTeamRequests, getMyTeamInvites, resolveMyTeamInvite, logout } from '../api/client';
import { CheckIcon, XMarkIcon, HourglassIcon, PersonIcon, DumbbellIcon, PeopleIcon, ScaleIcon, CameraIcon } from '../components/shared/Icons';
import { FacebookPill } from '../components/shared/FacebookPill';
import Skeleton from '../components/shared/Skeleton';
import EmptyState from '../components/shared/EmptyState';
import AsyncContent from '../components/shared/AsyncContent';
import ErrorState from '../components/shared/ErrorState';
import type { ComponentType } from 'react';
import { apiError } from '../hooks/useApiCall';
import { usePageTitle } from '../hooks/usePageTitle';
import { logBackgroundError } from '../observability';
import { signOutStamp } from '../lib/signOuts';
import { personName } from '../utils/personName';
import type { User, AthleteRecord, AthleteTeamInvite, AthleteTeamRequest } from '../types';
import AthleteFormFields, { EMPTY_ATHLETE_FORM, athleteFormFrom, toAthletePayload, type AthleteFormState } from '../components/AthleteFormFields';
import Field from '../components/shared/Field';

type RoleIcon = ComponentType<{ className?: string }>;
// #655 — a user asks for these two roles only. An admin gives every other
// role in the user editor. The server refuses the others too.
const REQUESTABLE_ROLES: { role: 'athlete' | 'head_coach'; Icon: RoleIcon }[] = [
  { role: 'athlete', Icon: PersonIcon },
  { role: 'head_coach', Icon: DumbbellIcon },
];

const PROFILE_TABS = ['profile', 'roles'] as const;

export default function Profile() {
  const { user, refreshAuth } = useAuthStore();
  // #674 — only an admin or a team manager can lower the bout count. The
  // athlete can keep or raise the own count. The name and the roles are those
  // of the backend check (`canLowerExperience` in lib/athleteExperience.ts,
  // #406). The selector does not clash with the `hasRole` variable of the
  // role cards below. The server decides: it answers 403, and `showError()`
  // shows the reason.
  const canLowerExperience = useAuthStore(s => s.hasRole('admin', 'team_manager'));
  const { t } = useI18n();
  // The team name of the join-request note is bold; the text around it comes from the dictionary.
  const [joinNoteBefore, joinNoteAfter] = t.profile.pendingJoinRequest.split('{team}');
  const { roleLabel } = useLabels();
  usePageTitle(t.nav.profile);
  const navigate = useNavigate();
  // #595 — the tab is in the URL: /profile?tab=roles opens the roles tab.
  const [tab, setTab] = useUrlTab(PROFILE_TABS, 'profile');
  // #605, #664 — one lock for each save, ONE lock for the photo upload and the
  // photo removal (two photo writes at one time can end in either order), and a
  // lock for each role request.
  const profileSave = useBusy();
  const athleteSave = useBusy();
  const photoWrite = useBusy();
  const roleRequests = useBusyKeys();
  // #666 — the sign-out shows the busy label. One sign-out runs at a time
  // (`logout()`), so a second tap here or in the header sends nothing.
  const signOut = useBusy();
  const photoInputRef = useRef<HTMLInputElement>(null);
  const { showError, showSuccess, showToast } = useToast();
  const { confirm } = useConfirm();
  const afterWrite = useAfterWrite();
  const [myRequests, setMyRequests] = useState<any[]>([]);
  const [requestNote, setRequestNote] = useState('');

  // User identity form (PATCH /api/users/me)
  const [form, setForm] = useState({
    first_name: '', last_name: '', phone: '', facebook_url: '',
  });

  // Athlete profile form (PATCH /api/athletes/me) — separate state, separate endpoint
  const [athleteForm, setAthleteForm] = useState<AthleteFormState>(EMPTY_ATHLETE_FORM);
  // The form value that the last prefill wrote, and the form of the newest render. The prefill
  // runs on `refresh()` too (after an invite approval): it must not overwrite unsaved edits.
  const prefilled = useRef(JSON.stringify(EMPTY_ATHLETE_FORM));
  const athleteFormNow = useRef(athleteForm);
  athleteFormNow.current = athleteForm;
  const fillAthleteForm = (next: AthleteFormState) => {
    prefilled.current = JSON.stringify(next);
    setAthleteForm(next);
  };
  const [myTeamRequests, setMyTeamRequests] = useState<AthleteTeamRequest[]>([]);
  // #658 — a coach invites the athlete; the athlete approves or rejects here.
  const [myInvites, setMyInvites] = useState<AthleteTeamInvite[]>([]);
  const inviteAnswers = useBusyKeys();

  // The read of the full user. The hook lets only the newest run write the state: a run that
  // React discarded (StrictMode runs the effect twice in dev), a run that the unmount left and
  // an older Retry are dropped (profile-load-race.spec.ts). The answer fills the identity form.
  const me = useLoad(() => getMe().then(r => r.data), [], {
    context: 'Profile:getMe',
    onData: u => setForm({
      first_name: u.first_name || '',
      last_name: u.last_name || '',
      phone: u.phone || '',
      facebook_url: u.facebook_url || '',
    }),
  });
  const fullUser = me.data ?? null;
  const setFullUser = me.setData;
  // The identity form stays off until the read of the full user filled it. A blank form that
  // looks editable sends "" for the phone and the Facebook link, and the save clears both.
  const formLoaded = !!me.data;
  const meFailed = !!me.error;

  // The read of the own athlete record. A 200 answer with `null` is the answer "no athlete
  // profile" (GET /api/athletes/me): it shows the empty state, a failure does not (#665, bug 89).
  const ownAthlete = useLoad(() => getMyAthlete().then(r => r.data), [], {
    context: 'Profile:getMyAthlete',
    initial: null as AthleteRecord | null,
    onData: ar => {
      const dirty = JSON.stringify(athleteFormNow.current) !== prefilled.current;
      if (ar && !dirty) fillAthleteForm(athleteFormFrom(ar));
    },
  });
  const athlete = ownAthlete.data;
  const setAthlete = ownAthlete.setData;

  useEffect(() => {
    // The three list reads have no load state. Only the live run writes the state: the answer of
    // the run that React discarded (StrictMode) must not draw over a newer state.
    let live = true;
    getMyTeamRequests().then(r => { if (live) setMyTeamRequests(r.data); })
      .catch(logBackgroundError('Profile:getMyTeamRequests'));
    getMyTeamInvites().then(r => { if (live) setMyInvites(r.data); })
      .catch(logBackgroundError('Profile:getMyTeamInvites'));
    api.get('/api/users/me/role-requests').then(r => { if (live) setMyRequests(r.data); }).catch(logBackgroundError('Profile:role-requests'));
    return () => { live = false; };
  }, []);

  async function answerInvite(inv: AthleteTeamInvite, status: 'approved' | 'rejected') {
    await inviteAnswers.run(inv.id, async () => {
      try {
        await resolveMyTeamInvite(inv.id, status);
      } catch (err) { showError(err); return; }
      setMyInvites(list => list.filter(i => i.id !== inv.id));
      // An approval moves the record to the team: read the record again.
      await afterWrite({
        source: 'Profile:invite',
        success: (status === 'approved' ? t.profile.teamInvites.approved : t.profile.teamInvites.rejected).replace('{team}', inv.team_name),
        refresh: status === 'approved' ? ownAthlete.refresh : undefined,
      });
    });
  }

  async function saveProfile(e: React.FormEvent) {
    e.preventDefault();
    if (!formLoaded) return; // the form is blank until the read of the user ends
    await profileSave.run(async () => {
      // #666 — The stamp before the request: a sign-out after it makes the answer old.
      const stamp = signOutStamp();
      try {
        const res = await api.patch<User>('/api/users/me', {
          first_name: form.first_name,
          last_name: form.last_name,
          // A cleared field is `null` (#452). JSON drops an `undefined` key, so
          // a cleared phone never reached the PATCH, the page said "Saved", and
          // the old number stayed public on the team page.
          phone: form.phone || null,
          facebook_url: form.facebook_url || null,
        });
        refreshAuth(res.data, stamp);
        setFullUser(res.data);
        showSuccess(t.profile.saved);
      } catch (err) { showError(err); }
    });
  }

  async function saveAthleteProfile(e: React.FormEvent) {
    e.preventDefault();
    if (!athlete) return; // first-time creation goes through the Teams page modal
    await athleteSave.run(async () => {
      try {
        // #627 — the experience and the weight go only when the user changed
        // them: a verdict or a weigh-in can write them while the page is open.
        const res = await updateMyAthlete(toAthletePayload(athleteForm, athleteFormFrom(athlete)));
        // The form shows the saved row, with the number of the verdict.
        setAthlete(res.data);
        fillAthleteForm(athleteFormFrom(res.data));
        showSuccess(t.profile.saved);
      } catch (err) { showError(err); }
    });
  }

  async function requestRole(role: string) {
    // #605 — the Request button of the role is disabled until the answer. A
    // second tap sends nothing.
    await roleRequests.run(role, async () => {
      const stamp = signOutStamp();
      try {
        const res = await api.post('/api/users/me/role-request', { requested_role: role, note: requestNote });
        setMyRequests(r => [res.data, ...r]);
        setRequestNote('');
        showSuccess(t.profile.roleRequested.replace('{role}', roleLabel(role)));
        // A self-granted role (#455): the page reads the roles of `fullUser`,
        // which loads once, so the card showed Request again and invited a
        // second request. /me passes through the 401 refresh (the grant made
        // the token stale) and returns the new roles. The role is granted
        // already, so a failure here is a background error, not a toast.
        // #605 — the lock also waits for the new roles. Before they come,
        // the card shows the Request button again.
        if (res.data?.auto_approved) {
          await getMe()
            .then(me => { refreshAuth(me.data, stamp); setFullUser(me.data); })
            .catch(logBackgroundError('Profile:me-after-grant'));
        }
      } catch (err) { showError(err); }
    });
  }

  if (!user) return null;
  const displayUser = (fullUser || user) as User;
  const currentRoles: string[] = displayUser.roles || user.roles;
  const getRequestStatus = (role: string) => myRequests.find(r => r.requested_role === role)?.status;

  return (
    <div className="max-w-2xl mx-auto space-y-6">

      {/* Header. #552 — on a phone the avatar is smaller and Sign out has
          its own row, so the name and the email have the width of the card. */}
      <div className="card p-4 sm:p-6 flex flex-wrap sm:flex-nowrap items-center gap-4 sm:gap-5">

        {/* Clickable avatar — opens file picker */}
        <div className="relative shrink-0 group">
          <Avatar
            firstName={displayUser.first_name} lastName={displayUser.last_name}
            photoUrl={displayUser.photo_url} size="xl"
            className="ring-4 ring-white shadow-md max-sm:w-16 max-sm:h-16 max-sm:text-lg"
          />
          <button
            type="button"
            onClick={() => photoInputRef.current?.click()}
            disabled={photoWrite.busy}
            className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
            title={t.profile.changePhoto}
            aria-label={t.profile.changePhoto}
          >
            {photoWrite.busy
              ? <span className="text-white text-xs">...</span>
              : <CameraIcon className="w-5 h-5 text-white" />}
          </button>
          {/* #552 — a touch screen has no hover: this badge shows that a tap
              on the avatar changes the photo. */}
          <span aria-hidden="true"
            className="hidden max-sm:flex pointer-coarse:flex absolute -bottom-0.5 -right-0.5 w-7 h-7 rounded-full bg-gray-700 text-white ring-2 ring-white items-center justify-center pointer-events-none">
            <CameraIcon className="w-4 h-4" />
          </span>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                await photoWrite.run(async () => {
                  const stamp = signOutStamp();
                  // #666 — the user of the request. The answer changes the photo of this user only.
                  const ownerId = useAuthStore.getState().user?.id;
                  try {
                    const res = await uploadAvatar(file);
                    const newUrl = res.url;
                    setFullUser(u => u && ({ ...u, photo_url: newUrl }));
                    // The stored user comes from the store at the answer, not from the render.
                    const current = useAuthStore.getState().user;
                    if (current && current.id === ownerId) refreshAuth({ ...current, photo_url: newUrl }, stamp);
                    showSuccess(t.profile.photoUpdated);
                  } catch (err) {
                    showError(err);
                  }
                });
              } finally {
                e.target.value = '';
              }
            }}
          />
        </div>

        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-black truncate">{personName(displayUser.first_name, displayUser.last_name)}</h1>
          <p className="text-sm text-gray-500 truncate">{displayUser.email}</p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {currentRoles.map((role: string) => (
              <RoleBadge key={role} role={role} className="whitespace-nowrap" />
            ))}
          </div>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {displayUser.photo_url && (
              <button
                type="button"
                // #605 — the button is disabled until the answer of the DELETE. The
                // photo upload is disabled too: two photo writes at one time can
                // end in either order (one lock, `photoWrite`).
                onClick={async () => {
                  // #665 — ask BEFORE the lock.
                  if (photoWrite.busy) return;
                  if (!(await confirm({ message: t.profile.removePhotoConfirm, confirmLabel: t.common.remove, destructive: true }))) return;
                  await photoWrite.run(async () => {
                  const stamp = signOutStamp();
                  // #666 — the user of the request. The answer changes the photo of this user only.
                  const ownerId = useAuthStore.getState().user?.id;
                  try {
                    await deleteAvatar();
                    setFullUser(u => u && ({ ...u, photo_url: undefined }));
                    // The stored user comes from the store at the answer, not from the render.
                    const current = useAuthStore.getState().user;
                    if (current && current.id === ownerId) refreshAuth({ ...current, photo_url: undefined }, stamp);
                    showSuccess(t.profile.photoRemoved);
                  } catch (err) { showError(err); }
                  });
                }}
                disabled={photoWrite.busy}
                className="text-xs text-red-500 hover:underline disabled:opacity-50"
              >
                {t.profile.removePhoto}
              </button>
            )}
          </div>
          {displayUser.facebook_url && <FacebookPill url={displayUser.facebook_url} testId="profile-facebook" className="mt-1" />}
        </div>
        <button onClick={() => signOut.run(async () => {
          const { complete, retryAtNextLoad } = await logout();
          // #666 — The POST got no 2xx answer. The page goes to Home now, so the warning
          // stays until the user closes it. It names the resend of the next page load
          // only when the storage kept the key.
          if (!complete) {
            showToast(retryAtNextLoad ? t.nav.signOutIncomplete : t.nav.signOutIncompleteNoRetry, 'warning', { untilClosed: true });
          }
          navigate('/');
        })} disabled={signOut.busy}
          data-testid="profile-sign-out"
          className="btn-outline-red btn-sm w-full sm:w-auto shrink-0 sm:self-start">
          {signOut.busy ? t.nav.signingOut : t.profile.signOut}
        </button>
      </div>

      {/* #616 — an official opens the own statistics. */}
      {currentRoles.some(r => r === 'referee' || r === 'jury') && (
        <Link to={`/officials/${user.id}`} data-testid="profile-official-stats"
          className="btn-outline btn-sm inline-flex items-center gap-1.5">
          <ScaleIcon className="w-3.5 h-3.5" /> {t.officials.myStats}
        </Link>
      )}

      {/* Tabs */}
      <TabBar activeTab={tab} onChange={setTab} tabs={[
        { id: 'profile', label: t.profile.myProfile },
        { id: 'roles', label: t.profile.requestRole },
      ]} />


      {/* ── Profile tab ─────────────────────────────────────────────── */}
      {tab === 'profile' && (
        <>
          {/* User identity (account) section */}
          <div className="card p-6">
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-4">{t.profile.myProfile}</h2>
            {meFailed && <div className="mb-4"><ErrorState compact onRetry={me.reload} /></div>}
            <form onSubmit={saveProfile} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label={t.profile.firstName}>
                  <input className="input" value={form.first_name} disabled={!formLoaded}
                    onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
                </Field>
                <Field label={t.profile.lastName}>
                  <input className="input" value={form.last_name} disabled={!formLoaded}
                    onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
                </Field>
              </div>
              <Field label={t.profile.phone}>
                <input type="tel" className="input" placeholder="+370 600 00000" value={form.phone} disabled={!formLoaded}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
              </Field>
              <Field label={t.profile.facebookUrl}>
                <input type="url" className="input" placeholder="https://facebook.com/yourprofile" value={form.facebook_url} disabled={!formLoaded}
                  onChange={e => setForm(f => ({ ...f, facebook_url: e.target.value }))} />
              </Field>
              <button type="submit" disabled={profileSave.busy || !formLoaded} className="btn-primary btn-sm" data-testid="profile-save">
                {profileSave.busy ? t.common.loading : t.profile.saveProfile}
              </button>
            </form>
          </div>

          {/* Athlete profile section — separate endpoint, only if user has an AR */}
          <div className="card p-6" data-testid="profile-athlete-card">
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-4">Athlete profile</h2>
            {myInvites.length > 0 && (
              <div className="mb-4 space-y-2" data-testid="profile-team-invites">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{t.profile.teamInvites.title}</div>
                {myInvites.map(inv => (
                  <div key={inv.id} className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm flex flex-wrap sm:flex-nowrap items-center gap-2">
                    <span className="flex-1 min-w-0 text-blue-900">
                      {t.profile.teamInvites.invites.replace('{team}', inv.team_name)}
                    </span>
                    <ResolveActions busy={inviteAnswers.isBusy(inv.id)} onResolve={status => answerInvite(inv, status)} />
                  </div>
                ))}
              </div>
            )}
            {/* Pending team-join requests — surface them so the athlete sees their
             *  request is waiting for coach/admin/TM approval. Only "pending" shows;
             *  approved/rejected fall off. */}
            {myTeamRequests.filter(r => r.status === 'pending').length > 0 && (
              <div className="mb-4 space-y-2">
                {myTeamRequests.filter(r => r.status === 'pending').map(req => (
                  <div key={req.id} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm flex items-center gap-2">
                    <HourglassIcon className="w-4 h-4 text-amber-600 shrink-0" />
                    <span className="text-amber-900">
                      {joinNoteBefore}<span className="font-semibold">{req.team_name || t.common.team}</span>{joinNoteAfter}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <AsyncContent
              loading={ownAthlete.loading}
              error={!!ownAthlete.error}
              onRetry={ownAthlete.reload}
              compact
              isEmpty={!athlete}
              skeleton={<Skeleton.Section lines={4} />}
              empty={<EmptyState
                compact
                icon={<PersonIcon className="w-10 h-10" />}
                title={t.profile.noAthleteProfile}
                hint={t.common.empty.profileAthleteHint}
                action={
                  <Link to="/teams" className="btn-outline btn-sm inline-flex items-center gap-1">
                    <PeopleIcon className="w-4 h-4" /> {t.profile.browseTeams}
                  </Link>
                }
              />}
            >
              <form onSubmit={saveAthleteProfile} className="space-y-4">
                {/* requireAll: before, the form sent no key for an empty gender or
                    birth date, and the page said "Saved" while the old value
                    stayed (#452 review). */}
                <AthleteFormFields value={athleteForm} onChange={setAthleteForm} requireAll
                  minExperience={canLowerExperience ? undefined : (athlete?.experience_bouts ?? 0)} />
                <button type="submit" disabled={athleteSave.busy} className="btn-primary btn-sm">
                  {athleteSave.busy ? t.common.loading : t.profile.saveProfile}
                </button>
              </form>
            </AsyncContent>
          </div>
        </>
      )}

      {/* ── Request Role tab ─────────────────────────────────────── */}
      {tab === 'roles' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">{t.profile.roleRequestHelp}</p>
          <Field label={t.profile.noteForAdmin}>
            <input className="input" placeholder={t.profile.notePlaceholder}
              value={requestNote} onChange={e => setRequestNote(e.target.value)} />
          </Field>
          <div className="space-y-3">
            {REQUESTABLE_ROLES.map(({ role, Icon }) => {
              const description = role === 'athlete' ? t.profile.athleteRoleHelp : t.profile.headCoachRoleHelp;
              const hasRole = currentRoles.includes(role);
              const reqStatus = getRequestStatus(role);
              return (
                <div key={role} data-testid={`role-card-${role}`} className={`card p-4 flex items-center gap-4 ${hasRole ? 'opacity-60' : ''}`}>
                  <div className="w-10 flex justify-center shrink-0 text-gray-700"><Icon className="w-7 h-7" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{roleLabel(role)}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{description}</div>
                  </div>
                  <div className="shrink-0">
                    {hasRole ? (
                      <span className="badge badge-green text-xs inline-flex items-center gap-1"><CheckIcon className="w-3 h-3" /> {t.profile.alreadyHave}</span>
                    ) : reqStatus === 'pending' ? (
                      <span className="badge badge-amber text-xs inline-flex items-center gap-1"><HourglassIcon className="w-3 h-3" /> {t.profile.pendingApproval}</span>
                    ) : (
                      // #655 — after a rejection the user can ask again: the
                      // API accepts a new request, and an admin rejects Head
                      // Coach requests now. The badge stays above the button.
                      <div className="flex flex-col items-end gap-1">
                        {reqStatus === 'rejected' && (
                          <span className="badge badge-gray text-xs inline-flex items-center gap-1"><XMarkIcon className="w-3 h-3" /> {t.profile.rejected}</span>
                        )}
                        <button onClick={() => requestRole(role)} disabled={roleRequests.isBusy(role)} className="btn-outline btn-sm">
                          {t.profile.requestButton}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {myRequests.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">{t.profile.requestHistory}</h3>
              <div className="space-y-2">
                {myRequests.map(r => (
                  <div key={r.id} className="card px-4 py-3 flex items-center gap-3 text-sm">
                    <div className="flex-1">
                      <span className="font-medium">{roleLabel(r.requested_role)}</span>
                      {r.note && <span className="text-gray-400 ml-2">"{r.note}"</span>}
                    </div>
                    <StatusBadge status={r.status} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
